import React, { useEffect, useMemo, useState } from 'react';
import { customFieldsApi } from '../services/api';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Stack,
  Tooltip,
  FormControl,
  InputLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SaveIcon from '@mui/icons-material/Save';

const FIELD_TYPES = [
  { value: 'TEXT', label: 'Текст' },
  { value: 'TEXTAREA', label: 'Текстовая область' },
  { value: 'DATETIME', label: 'Дата/время' },
  { value: 'MULTISELECT', label: 'Мультивыбор' },
  { value: 'CHECKBOX', label: 'Чекбокс' }
];

export default function CustomFieldsManager({ projectId }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openEditor, setOpenEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [form, setForm] = useState({
    label: '',
    name: '',
    type: 'TEXT',
    required: false,
    options: '',
    helpText: '',
    isActive: true,
    exportToReport: false
  });
  const [testValue, setTestValue] = useState('');

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await customFieldsApi.getProjectFields(projectId);
      setFields(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [projectId]);

  const handleOpenCreate = () => {
    setEditing(null);
    setForm({ label: '', name: '', type: 'TEXT', required: false, options: '', helpText: '', isActive: true, exportToReport: false });
    setOpenEditor(true);
  };
  const handleOpenEdit = (field) => {
    setEditing(field);
    setForm({
      label: field.label || '',
      name: field.name || '',
      type: field.type || 'TEXT',
      required: !!field.required,
      options: Array.isArray(field.options) ? field.options.join(', ') : (field.options?.join ? field.options.join(', ') : (Array.isArray(field.options) ? field.options.join(', ') : (field.options ? String(field.options) : ''))),
      helpText: field.helpText || '',
      isActive: !!field.isActive,
      exportToReport: !!field.exportToReport
    });
    setOpenEditor(true);
  };
  const handleCloseEditor = () => setOpenEditor(false);

  const parsedOptions = useMemo(() => {
    if (form.type !== 'MULTISELECT') return null;
    return form.options
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }, [form.type, form.options]);

  const save = async () => {
    const payload = {
      label: form.label.trim(),
      name: form.name.trim(),
      type: form.type,
      required: !!form.required,
      options: form.type === 'MULTISELECT' ? parsedOptions : null,
      helpText: form.helpText?.trim() || null,
      isActive: !!form.isActive,
      exportToReport: !!form.exportToReport
    };
    if (!payload.label || !payload.name) return;

    if (editing) {
      await customFieldsApi.update(editing.id, payload);
    } else {
      await customFieldsApi.create(projectId, payload);
    }
    setOpenEditor(false);
    await load();
  };

  const remove = async (field) => {
    if (!window.confirm('Удалить поле?')) return;
    await customFieldsApi.delete(field.id);
    await load();
  };

  // Простое управление порядком: кнопки вверх/вниз + сохранение
  const moveUp = (index) => {
    if (index <= 0) return;
    const arr = [...fields];
    [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
    setFields(arr);
    setIsReordering(true);
  };
  const moveDown = (index) => {
    if (index >= fields.length - 1) return;
    const arr = [...fields];
    [arr[index + 1], arr[index]] = [arr[index], arr[index + 1]];
    setFields(arr);
    setIsReordering(true);
  };
  const saveOrder = async () => {
    const fieldOrder = fields.map(f => f.id);
    await customFieldsApi.reorder(projectId, fieldOrder);
    setIsReordering(false);
    await load();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Кастомные поля</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isReordering && (
            <Button variant="outlined" size="small" startIcon={<SaveIcon />} onClick={saveOrder}>
              Сохранить порядок
            </Button>
          )}
          <Button variant="contained" onClick={handleOpenCreate}>Добавить поле</Button>
        </Box>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={56}></TableCell>
            <TableCell>Порядок</TableCell>
            <TableCell>Метка</TableCell>
            <TableCell>Ключ</TableCell>
            <TableCell>Тип</TableCell>
            <TableCell>Обязательное</TableCell>
            <TableCell>Активно</TableCell>
            <TableCell>В отчёт</TableCell>
            <TableCell align="right">Действия</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((f, idx) => (
            <TableRow key={f.id} hover>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Tooltip title="Вверх">
                  <span>
                    <IconButton size="small" onClick={() => moveUp(idx)} disabled={idx === 0} aria-label="move up">
                      <KeyboardArrowUpIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Вниз">
                  <span>
                    <IconButton size="small" onClick={() => moveDown(idx)} disabled={idx === fields.length - 1} aria-label="move down">
                      <KeyboardArrowDownIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>{f.label}</TableCell>
              <TableCell>{f.name}</TableCell>
              <TableCell>{f.type}</TableCell>
              <TableCell>{f.required ? 'Да' : 'Нет'}</TableCell>
              <TableCell>{f.isActive ? 'Да' : 'Нет'}</TableCell>
              <TableCell>{f.exportToReport ? 'Да' : 'Нет'}</TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => handleOpenEdit(f)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" color="error" onClick={() => remove(f)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
          {fields.length === 0 && (
            <TableRow>
              <TableCell colSpan={9}>
                <Typography color="text.secondary">Нет полей</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Editor */}
      <Dialog open={openEditor} onClose={handleCloseEditor} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Редактирование поля' : 'Новое поле'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Метка" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} fullWidth />
            <TextField label="Ключ (латиница)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Тип поля</InputLabel>
              <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} label="Тип поля">
                {FIELD_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {form.type === 'MULTISELECT' && (
              <TextField label="Варианты (через запятую)" value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} fullWidth />
            )}
            {form.type === 'DATETIME' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Тестовое значение"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={testValue}
                  onChange={(e) => setTestValue(e.target.value)}
                />
                                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const now = new Date();
                      const year = now.getFullYear();
                      const month = String(now.getMonth() + 1).padStart(2, '0');
                      const day = String(now.getDate()).padStart(2, '0');
                      const hours = String(now.getHours()).padStart(2, '0');
                      const minutes = String(now.getMinutes()).padStart(2, '0');
                      const datetimeLocal = `${year}-${month}-${day}T${hours}:${minutes}`;
                      setTestValue(datetimeLocal);
                    }}
                    sx={{ 
                      minWidth: 'auto', 
                      px: 2, 
                      height: 56,
                      position: 'relative',
                      top: 8
                    }}
                  >
                    Сейчас
                  </Button>
              </Box>
            )}
            <TextField label="Подсказка" value={form.helpText} onChange={(e) => setForm({ ...form, helpText: e.target.value })} fullWidth />
            <Box>
              <FormControlLabel control={<Checkbox checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} />} label="Обязательное" />
              <FormControlLabel control={<Checkbox checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />} label="Активно" />
              <FormControlLabel control={<Checkbox checked={form.exportToReport} onChange={(e) => setForm({ ...form, exportToReport: e.target.checked })} />} label="Экспортировать в отчёт" />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditor}>Отмена</Button>
          <Button onClick={save} variant="contained">Сохранить</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


